/**
 * One approved send, as a person needs to see it (MA-484): "Sending in Ns" with
 * a working Undo while the window runs, then the outcome read from the row:
 * Sent, back for review (and why), or an unknown outcome with the three Resolve
 * actions. A failed Undo or Resolve is shown as failed, never as success.
 */
import { useState } from 'react';
import { Button } from '@archieai/ui';
import { X, Undo2, Link2, RotateCcw, Ban } from 'lucide-react';
import { StatusBadge } from '../shared/Panel';
import type { AgentDraft, ResolveDraftAction } from '../../api/comms';
import { isSettled, phaseLabel, type SendView } from './mailSend';

type BadgeVariant = 'active' | 'success' | 'error' | 'warning' | 'neutral' | 'info';

const PHASE_BADGE: Record<SendView['phase'], BadgeVariant> = {
  undo_window: 'active',
  sending: 'info',
  sent: 'success',
  returned: 'warning',
  cancelled: 'neutral',
  uncertain: 'error',
  unknown: 'warning',
};

function formatTime(iso: string | null): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return new Date(t).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export interface MailSendRowProps {
  draft: AgentDraft;
  view: SendView;
  notice?: { kind: 'error' | 'info'; text: string } | null;
  busy?: 'undo' | 'resolve' | null;
  onUndo?: () => void;
  onResolve?: (action: ResolveDraftAction) => void;
  onDismiss?: () => void;
}

export function MailSendRow({ draft, view, notice, busy, onUndo, onResolve, onDismiss }: MailSendRowProps) {
  const [linking, setLinking] = useState(false);
  const [messageId, setMessageId] = useState('');
  const toAddr = draft.to_addresses?.[0] ?? '';
  const sentAt = view.phase === 'sent' ? formatTime(view.sentAt) : null;
  const settled = isSettled(view);

  return (
    <div className="rounded-xl border border-neutral-200/80 px-4 py-3" data-testid="mail-send-row" data-draft-id={draft.id} data-phase={view.phase}>
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] text-neutral-400 truncate">{toAddr}</p>
          <p className="text-sm text-neutral-700 truncate">{draft.subject}</p>
        </div>
        <StatusBadge variant={view.contentMismatch ? 'warning' : PHASE_BADGE[view.phase]}>
          <span data-testid="mail-send-status">{phaseLabel(view)}</span>
        </StatusBadge>
        {sentAt && <span className="text-[11px] text-neutral-400 shrink-0 tabular-nums">{sentAt}</span>}
        {view.canUndo && onUndo && (
          <Button variant="ghost" size="sm" startIcon={<Undo2 size={12} />} onClick={onUndo} disabled={busy === 'undo'}>
            {busy === 'undo' ? 'Undoing…' : `Undo (${view.secondsLeft}s)`}
          </Button>
        )}
        {settled && onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="p-1 text-neutral-400 hover:text-neutral-600 rounded"
            title="Dismiss"
            aria-label="Dismiss"
          >
            <X size={12} />
          </button>
        )}
      </div>

      {view.reason && view.phase !== 'undo_window' && (
        <p className="mt-1.5 text-[11px] text-neutral-500 leading-relaxed" data-testid="mail-send-reason">{view.reason}</p>
      )}

      {view.canResolve && onResolve && (
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="mail-send-resolve">
          {linking ? (
            <>
              <input
                value={messageId}
                onChange={e => setMessageId(e.target.value)}
                placeholder="Gmail message id of the sent message"
                aria-label="Sent message id"
                className="text-xs border border-neutral-200 rounded px-2 py-1 min-w-[16rem]"
              />
              <Button
                variant="ordinary"
                size="sm"
                startIcon={<Link2 size={12} />}
                disabled={!messageId.trim() || busy === 'resolve'}
                onClick={() => onResolve({ action: 'link', message_id: messageId.trim() })}
              >
                Link
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setLinking(false)}>Cancel</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" startIcon={<Link2 size={12} />} disabled={busy === 'resolve'} onClick={() => setLinking(true)}>
                It was sent
              </Button>
              <Button variant="ghost" size="sm" startIcon={<RotateCcw size={12} />} disabled={busy === 'resolve'} onClick={() => onResolve({ action: 'return' })}>
                Return for review
              </Button>
              <Button variant="ghost" size="sm" startIcon={<Ban size={12} />} disabled={busy === 'resolve'} onClick={() => onResolve({ action: 'reject' })}>
                Reject
              </Button>
            </>
          )}
        </div>
      )}

      {notice && (
        <p
          role={notice.kind === 'error' ? 'alert' : 'status'}
          className={`mt-1.5 text-[11px] leading-relaxed ${notice.kind === 'error' ? 'text-red-600' : 'text-neutral-500'}`}
          data-testid="mail-send-notice"
        >
          {notice.text}
        </p>
      )}
    </div>
  );
}
